import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private readonly isBrowser: boolean;

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) platformId: Object,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    if (!this.isBrowser) {
      return;
    }

    this.injectGtag();
    this.trackPageViews();
  }

  private injectGtag(): void {
    if (!environment.gaMeasurementId) {
      console.warn('GA Measurement ID not set in environment.');
      return;
    }

    const gtagScript = this.document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${environment.gaMeasurementId}`;
    this.document.head.appendChild(gtagScript);

    const inlineScript = this.document.createElement('script');
    inlineScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      window.gtag = function(){ window.dataLayer.push(arguments); }
      window.gtag('js', new Date());
      window.gtag('config', '${environment.gaMeasurementId}');
    `;

    this.document.head.appendChild(inlineScript);
  }

  private trackPageViews(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (!this.isBrowser || typeof window.gtag !== 'function') {
          return;
        }

        window.gtag('event', 'page_view', {
          page_path: event.urlAfterRedirects,
          page_location: window.location.href,
          page_title: this.document.title
        });
      });
  }

  logEvent(eventName: string, params: Record<string, any> = {}): void {
    if (!this.isBrowser || typeof window.gtag !== 'function') {
      return;
    }

    window.gtag('event', eventName, params);
  }

  setUserProperties(properties: Record<string, any>): void {
    if (!this.isBrowser || typeof window.gtag !== 'function') {
      return;
    }

    window.gtag('set', 'user_properties', properties);
  }
}